import type { GenerationSummary, GenerationsPage } from '../model/types';

/**
 * Кэш бесконечного списка генераций — чистые функции над данными `useInfiniteQuery`
 * (страницы и курсоры). Без alias-импортов и без React Query в рантайме — их проверяют тесты.
 */
export interface GenerationsData {
  pages: GenerationsPage[];
  pageParams: (string | null)[];
}

export function findGenerationSummary(data: Pick<GenerationsData, 'pages'> | undefined, id: string): GenerationSummary | null {
  if (!data) return null;
  for (const page of data.pages) {
    const found = page.items.find((s) => s.id === id);
    if (found) return found;
  }
  return null;
}

/** Все загруженные сводки по порядку (дубли страниц — один раз). Принимает и данные `useInfiniteQuery`. */
export function listGenerationSummaries(data: Pick<GenerationsData, 'pages'> | undefined): GenerationSummary[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: GenerationSummary[] = [];
  for (const page of data.pages) {
    for (const s of page.items) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push(s);
    }
  }
  return out;
}

const time = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
};

/** Сменились ли статус, счётчики или жетоны записи — тогда открытую деталь пора перечитать. Жетоны — тоже: возврат
 *  сервер помечает отдельной записью, бывает позже статуса (опечатка, нераспознанное слово), и сводка, где сменились
 *  только `tokens`, приходит отдельно; без перечитки строка о жетоне в детали (по `items[].token`) осталась бы
 *  прежней. `updatedAt` признаком не служит: при пересчёте статуса из карточек сервер его не меняет (контракт § 7.2).
 *  Прежней сводки нет — «сменились». */
export function summaryChanged(previous: GenerationSummary | null, next: GenerationSummary): boolean {
  if (!previous) return true;
  if (previous.status !== next.status || previous.processing !== next.processing) return true;
  if (previous.tokens.held !== next.tokens.held || previous.tokens.refunded !== next.tokens.refunded) return true;
  const a = previous.counts;
  const b = next.counts;
  return (Object.keys(b) as (keyof typeof b)[]).some((key) => a[key] !== b[key]);
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Одна и та же сводка по содержимому (разборщик каждый раз даёт новый объект): ничего не перерисовываем. */
export function sameSummary(a: GenerationSummary, b: GenerationSummary): boolean {
  if (a === b) return true;
  if (
    a.id !== b.id ||
    a.kind !== b.kind ||
    a.title !== b.title ||
    a.topicBatchId !== b.topicBatchId ||
    a.createdAt !== b.createdAt ||
    a.updatedAt !== b.updatedAt ||
    a.status !== b.status ||
    a.processing !== b.processing ||
    a.module?.id !== b.module?.id ||
    a.module?.name !== b.module?.name ||
    a.tokens.held !== b.tokens.held ||
    a.tokens.refunded !== b.tokens.refunded ||
    !sameList(a.fronts, b.fronts)
  ) {
    return false;
  }
  return (Object.keys(b.counts) as (keyof GenerationSummary['counts'])[]).every((key) => a.counts[key] === b.counts[key]);
}

/** Генерация ещё идёт: по ней нужен запасной опрос. */
export const isActiveSummary = (s: GenerationSummary) => s.status === 'generating' || s.processing;

/** Порядок списка — как у сервера: `created_at DESC, id DESC`. */
const newerThan = (a: GenerationSummary, b: GenerationSummary) => {
  const ta = time(a.createdAt);
  const tb = time(b.createdAt);
  return ta !== tb ? ta > tb : a.id > b.id;
};

export interface UpsertResult {
  data: GenerationsData | undefined;
  previous: GenerationSummary | null;
  inserted: boolean;
}

/**
 * Заменить сводку по id во всех страницах. Записи нет — встаёт по порядку (`createdAt`, `id`) в первую
 * страницу, если она новее её последней строки или страница одна и следующей нет: SSE чужой записи
 * (другая вкладка, другое устройство) может прийти раньше ответа «Создать», и своя запись — уже не самая
 * свежая. Старше первой страницы при загруженном продолжении — не вставляем: она придёт со своей
 * страницей. Список не загружен — данные не трогаем: сводка в кэш не встаёт, список перечитают
 * (ТЗ § 22.8). Ничего не изменилось — прежний объект, чтобы не будить подписчиков.
 */
export function upsertIntoPages(data: GenerationsData | undefined, summary: GenerationSummary): UpsertResult {
  if (!data || data.pages.length === 0) return { data, previous: null, inserted: false };
  const previous = findGenerationSummary(data, summary.id);
  if (previous) {
    if (sameSummary(previous, summary)) return { data, previous, inserted: false };
    return {
      data: {
        ...data,
        pages: data.pages.map((page) =>
          page.items.some((s) => s.id === summary.id)
            ? { ...page, items: page.items.map((s) => (s.id === summary.id ? summary : s)) }
            : page,
        ),
      },
      previous,
      inserted: false,
    };
  }
  const [head, ...rest] = data.pages;
  let at = head.items.findIndex((s) => newerThan(summary, s));
  if (at < 0) {
    // Старше всех строк первой страницы: в конец — только если продолжения нет.
    if (rest.length > 0 || head.nextCursor) return { data, previous: null, inserted: false };
    at = head.items.length;
  }
  const items = [...head.items.slice(0, at), summary, ...head.items.slice(at)];
  return {
    data: { ...data, pages: [{ ...head, items }, ...rest] },
    previous: null,
    inserted: true,
  };
}

/** Сводка записи и момент, на который она верна: `computedAt` сервера (контракт § 7.2), а у старого бэкенда —
 *  часы устройства (у ответа — когда ушёл запрос, у SSE — когда пришло). */
export interface KnownSummary {
  summary: GenerationSummary;
  asOf: number;
}

/** На какой момент сводка верна: время подсчёта на сервере, а без него (старый бэкенд) — запасной момент
 *  по часам устройства. */
export const summaryAsOf = (summary: GenerationSummary, fallback: number): number => summary.computedAt ?? fallback;

/** Пришедшая сводка устарела: о записи уже известно состояние на более поздний момент. Так ответ опроса,
 *  посчитанный до SSE «готово», не откатывает запись обратно в «генерируем». */
export const isStaleSummary = (known: KnownSummary | undefined, asOf: number): boolean => !!known && known.asOf > asOf;

/**
 * Ответ `GET /generations?ids=…` (контракт § 7.2): записи, которых ученик больше не видит (модуль удалён,
 * скрыт), в нём просто отсутствуют — как 404 детали. Возвращает id таких записей; `null` — сервер `ids` не
 * знает (старый бэкенд отдал обычную страницу с чужими записями), и об отсутствующих ничего не известно.
 */
export function missingFromIdsAnswer(requested: readonly string[], answer: readonly GenerationSummary[]): string[] | null {
  const wanted = new Set(requested);
  if (answer.some((summary) => !wanted.has(summary.id))) return null;
  const present = new Set(answer.map((summary) => summary.id));
  return requested.filter((id) => !present.has(id));
}

/**
 * Сверить страницы ответа со свежими сводками (ТЗ § 22.9): пока шёл запрос страницы, могли прийти SSE — такие
 * строки ответа заменяются известной свежей сводкой, остальные запоминаются. Момент строки — её `computedAt`, а
 * без него — `requestedAt` страницы; страница без того и другого (вставка, правка кэша) не сверяется. Ничего не
 * поменялось — тот же объект: React Query не будит подписчиков.
 */
export function reconcilePages(data: GenerationsData, known: Map<string, KnownSummary>): GenerationsData {
  let changed = false;
  const pages = data.pages.map((page) => {
    let pageChanged = false;
    const items = page.items.map((summary) => {
      const asOf = summary.computedAt ?? page.requestedAt;
      if (asOf === undefined) return summary;
      const fresher = known.get(summary.id);
      if (fresher && isStaleSummary(fresher, asOf)) {
        if (sameSummary(fresher.summary, summary)) return summary;
        pageChanged = true;
        return fresher.summary;
      }
      known.set(summary.id, { summary, asOf });
      return summary;
    });
    if (!pageChanged) return page;
    changed = true;
    return { ...page, items };
  });
  return changed ? { ...data, pages } : data;
}

export function removeFromPages(data: GenerationsData | undefined, id: string): GenerationsData | undefined {
  if (!data || !findGenerationSummary(data, id)) return data;
  return {
    ...data,
    pages: data.pages.map((page) =>
      page.items.some((s) => s.id === id) ? { ...page, items: page.items.filter((s) => s.id !== id) } : page,
    ),
  };
}

/** Возврат на главную: окно снова пять записей, кэш — до первой страницы. Иначе React Query
 *  перечитал бы подряд все загруженные страницы. */
export function trimToFirstPage(data: GenerationsData | undefined): GenerationsData | undefined {
  if (!data || data.pages.length <= 1) return data;
  return { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) };
}
